import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useOutletContext } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import ApiScenariosManagement from "../../../api/scenarios-management";
import ApiSubjectsDashboard from "../../../api/subjects-dashboard";
import ScenarioTable from "../scenario-management/components/scenario-table";
import ScenarioPage from "../scenario-management/components/scenario-page";
import useNotification from "../../../components/notification/notification";
import { Body } from "../../../components/main-body/body";
import "./styles.css";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import ApiOnboardingManagement from "../../../api/onboarding-management";
import debounce from "../../../common/utils/debounce";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { useCan } from "../../../common/utils/usePermissions";

const normalizeScenarios = (scenarios) => {
  return scenarios.map((scenario) => ({
    ...scenario,
    name: scenario.title,
    ScenarioVersion: scenario.OnboardingSurveyVersion,
  }));
};

const ALLOWED_STATUSES = new Set(["active", "pending", "inactive"]);

function parseStatusesFromSearch(search) {
  const sp = new URLSearchParams(search || "");

  // ?statuses=pending
  // ?statuses=pending,active
  // ?statuses=pending&statuses=active
  const rawAll = sp.getAll("statuses").flatMap((v) => String(v).split(","));

  const statuses = rawAll
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => ALLOWED_STATUSES.has(v));

  return statuses.length ? statuses : null;
}

const OnboardingManagement = ({ mode }) => {
  const [scenarios, setScenarios] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  // const [total, setTotal] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState({});
  const [cohorts, setCohorts] = useState([]);
  const { t } = useTranslation();
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { openNotification } = useNotification();
  const [loading, setLoading] = useState();
  const { id } = useParams();
  const [cohortsList, setCohortsList] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const navigate = useProjectNavigate();
  const location = useLocation();

  // ✅ ініціалізуємо filters ОДРАЗУ з URL (тільки statuses)
  const [filters, setFilters] = useState(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);
    return statusesFromUrl ? { statuses: statusesFromUrl } : {};
  });

  const [sort, setSort] = useState({ sortField: null, sortOrder: null });
  const [changeLogs, setChangeLogs] = useState({});

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } = useBulkSelection(
    scenarios,
    {
      rowKey: "id",
    }
  );

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const outlet = useOutletContext();
  const setSidebarProps = outlet?.setSidebarProps ? outlet.setSidebarProps : () => {};

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })));
    });
  };

  useEffect(() => {
    if (selectedScenario?.id) {
      setSidebarProps({
        backOnly: true,
        onBack: handleDeselectScenario,
      });
    } else {
      setSidebarProps({ title: "Content", backOnly: false, onBack: undefined });
    }
    // Reset on unmount so the Content submenu isn't left hidden (backOnly)
    // when navigating to another Content tab while viewing a scenario.
    return () => {
      setSidebarProps({ title: "Content", backOnly: false, onBack: undefined });
    };
  }, [selectedScenario, setSidebarProps]);

  const getScenarios = async (deselect, query = "", opts = {}) => {
    const append = opts.append === true;
    setLoading(true);
    return ApiOnboardingManagement.getScenarios({ query, ...filters, ...sort, page })
      .then((r) => {
        const { onboardingSurveys, groupOnboardingSurveys, changeLogs, hasMore } = r.data;
        setCohorts(
          groupOnboardingSurveys.map((cohort) => ({
            id: cohort.id,
            label: cohort.EnrollmentFieldOption.value,
          }))
        );
        setChangeLogs(changeLogs || {});

        const pageScenarios = normalizeScenarios(onboardingSurveys);
        setScenarios((prev) => (append ? [...prev, ...pageScenarios] : pageScenarios));
        setHasMore(!!hasMore);

        if (selectedScenario.id && !deselect) {
          setSelectedScenario({});
          const pool = append ? [...scenarios, ...pageScenarios] : pageScenarios;
          setSelectedScenario(pool.find((s) => s.id === selectedScenario.id) || {});
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getDailyTasks = async () => {
    return ApiScenariosManagement.getDailyTasks().then((r) => {
      setTasks(r.data.dailyTasks);
    });
  };

  const refreshCohorts = async () => {
    getDashboardFilterItems();

    if (page !== 1) {
      setPage(1);
      return;
    }

    await getScenarios(false, searchValue, { append: false });
  };

  useAdminEvents({
    onCohortChanged: refreshCohorts,
    onOnboardingChanged: (payload) => {
      const onboardingSurvey = payload?.onboardingSurvey;
      const nextScenario = onboardingSurvey
        ? normalizeScenarios([onboardingSurvey])[0]
        : null;
      const isInlineUpdate = payload?.action === "updated" && nextScenario;

      if (isInlineUpdate) {
        setScenarios((prev) =>
          (prev || []).map((scenario) =>
            scenario.id === nextScenario.id
              ? { ...scenario, ...nextScenario }
              : scenario
          )
        );
      } else if (page !== 1) {
        setPage(1);
      } else {
        getScenarios(false, searchValue, { append: false });
      }

      setSelectedScenario((prev) => {
        if (!prev?.id) return prev;

        const changedIds = [
          ...(payload?.deletedIds || []),
          payload?.id,
        ].filter(Boolean);

        if (!changedIds.includes(prev.id)) return prev;

        if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
          handleNavigate("/content/onboardings");
          return {};
        }

        return nextScenario ? { ...prev, ...nextScenario } : prev;
      });
    },
  });

  const handleDuplicate = (scenario) => {
    setLoading(true);
    ApiOnboardingManagement.duplicateScenario(scenario.id)
      .then(() => {
        getScenarios().then(() => {
          openNotification({
            type: "success",
            message: (
              <p>
                {t("administration.scenario.notification.duplicated", {
                  scenario: scenario.title,
                })}
              </p>
            ),
          });
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t("administration.scenario.notification.not_implemented", {
                scenario: "",
              })}
            </p>
          ),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateSorting = (sortField, sortOrder) => {
    setSort({ sortField, sortOrder });
  };

  useEffect(() => {
    getScenarios();
    getDailyTasks();
    getDashboardFilterItems();
  }, []);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      getScenarios(false, searchValue, { append: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort]);

  useEffect(() => {
    getScenarios(false, searchValue, { append: page > 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (id && scenarios.length) {
      const scenario = scenarios.find((scenario) => scenario.id === id);
      if (scenario) {
        setSelectedScenario(scenario);
        setIsOpen(true);
        handleNavigate(`/content/onboardings/${scenario.id}`);
      }
    } else if (mode !== "show") {
      setSelectedScenario({});
      setIsOpen(false);
    }
  }, [id, scenarios]);

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const createScenario = () => {
    setLoading(true);
    ApiOnboardingManagement.createScenario()
      .then((r) => {
        openNotification({
          type: "success",
          message: (
            <p>
              {t("administration.scenario.notification.created", {
                scenario: r.data.onboardingSurvey.title,
              })}
            </p>
          ),
        });

        if (page !== 1) {
          setPage(1);
        } else {
          getScenarios(false, searchValue, { append: false });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteScenario = (id, value) => {
    setLoading(true);
    ApiOnboardingManagement.deleteScenario(id)
      .then(() => {
        setPage(1);
        getScenarios(false, searchValue, { append: false }).then(() => {
          openNotification({
            type: "success",
            message: (
              <p>
                {t("administration.scenario.notification.deleted", {
                  scenario: value,
                })}
              </p>
            ),
          });
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: (
            <p>
              {t("administration.scenario.notification.not_implemented", {
                scenario: "",
              })}
            </p>
          ),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiOnboardingManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: <p>{deletedIds.length} {plural ? "items were" : "item was"} deleted</p>,
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: <p>Unable to delete {failedIds.length} {plural ? "items" : "item"}</p>,
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      if (page !== 1) {
        setPage(1);
      } else {
        await getScenarios(false, searchValue, { append: false });
      }
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete items</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    clearSelection,
    setSelectedKeys,
    page,
    searchValue,
  ]);

  const handleSelectScenario = (scenario) => {
    setSelectedScenario(scenario);
    setIsOpen(true);
    handleNavigate(`/content/onboardings/${scenario.id}`);
  };

  const handleDeselectScenario = async () => {
    setSelectedScenario({});
    handleNavigate(`/content/onboardings`);
    await getScenarios(true);
  };

  const debouncedGetScenarios = useCallback(
    debounce((deselect, value) => {
      setPage(1);
      getScenarios(deselect, value, { append: false });
    }, 400),
    [filters, sort, selectedScenario.id]
  );

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    debouncedGetScenarios(false, value);
  };

  const headerProps = useMemo(
    () => ({
      title: "Onboarding management",
      create: canCreate && {
        label: t("administration.onboarding.create"),
        onClick: createScenario,
      },
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
      },
    }),
    [isOpen, selectedScenario, createScenario, searchValue, t, canCreate]
  );

  const updateFilterValue = (value) => setFilters(value);
  const statuses = [
    { id: "active", name: "Active" },
    { id: "pending", name: "Pending" },
    { id: "inactive", name: "Inactive" },
  ];

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          mode="controlled"
          filter={filters}
          filtersBy={[
            { name: "Cohort", id: "cohortIds", children: cohortsList },
            { name: "Status", id: "statuses", children: statuses },
          ]}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      {selectedScenario?.id ? (
        <ScenarioPage
          scenario={selectedScenario}
          toList={handleDeselectScenario}
          refreshScenario={getScenarios}
          openNotification={openNotification}
          api={ApiOnboardingManagement}
          cohorts={cohorts}
          tasks={tasks}
          isOnboarding
          changeLogs={changeLogs}
        />
      ) : (
        <>
          <ScenarioTable
            updateSorting={updateSorting}
            data={scenarios}
            onHandleRowClick={handleSelectScenario}
            deleteScenario={deleteScenario}
            isOnboarding
            handleDuplicate={handleDuplicate}
            loading={loading}
            hasMore={hasMore}
            fetchData={() => {
              if (!loading && hasMore) setPage((p) => p + 1);
            }}
            selectionColumn={getSelectionColumn()}
            containerRef={tableRef}
          />
          <BulkSelectionBar
            left={bulkLeft}
            count={selectedKeys.length}
            onClear={clearSelection}
            onDelete={handleBulkDelete}
            loading={isBulkDeleting}
          />
        </>
      )}
    </Body>
  );
};

export default OnboardingManagement;
